import { useState } from "react";
import {
  Ban,
  Check,
  Copy,
  Globe,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  Server,
  Smartphone,
  X,
} from "lucide-react";
import { Button, Card } from "@/ui";
import { cn } from "@/lib/cn";
import { useWorkspace, type WsProperty } from "@/app/WorkspaceContext";
import { useCreateSite, useRevokeSite, useRotateSiteKey, useSites } from "@/data/hooks";
import type { Site, SiteType } from "@/data/services";
import { ApiError } from "@/api/client";
import { SampleChip, SkeletonLine, StatusPill } from "./parts";

/* ------------------------------- view model ------------------------------- */
/** One property as the rows render it — real or sample, normalized. */
interface PropView {
  siteId: string;
  name: string;
  type: SiteType;
  last4: string;
  revoked: boolean;
  origins: string[];
  urls: string[];
  sample: boolean;
}

const TYPE_META: Record<SiteType, { label: string; Icon: typeof Globe }> = {
  web: { label: "Web", Icon: Globe },
  ios: { label: "iOS", Icon: Smartphone },
  android: { label: "Android", Icon: Smartphone },
  server: { label: "Server", Icon: Server },
};
const TYPE_ORDER: SiteType[] = ["web", "ios", "android", "server"];

function liveView(s: Site): PropView {
  return {
    siteId: s.site_id,
    name: s.name,
    type: s.type,
    last4: s.write_key_last4,
    revoked: !!s.revoked_at,
    origins: s.allowed_origins ?? [],
    urls: s.urls ?? [],
    sample: false,
  };
}
function sampleView(p: WsProperty): PropView {
  return {
    siteId: p.id,
    name: p.name,
    type: p.kind,
    last4: "0000",
    revoked: false,
    origins: [],
    urls: p.domain ? [p.domain] : [],
    sample: true,
  };
}

/** Pull a human-readable message out of a failed mutation. */
function errMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string } | string | null;
    if (body && typeof body === "object" && body.message) return body.message;
  }
  return fallback;
}

/** What the one-time key callout needs to render. */
interface Reveal {
  name: string;
  siteId: string;
  key: string;
  reason: "created" | "rotated";
}

/** Ready-to-paste first-party web tag, with the REAL write key substituted.
 *  The Integrations tab can only ever show a `YOUR_WRITE_KEY` placeholder because
 *  the plaintext key is unrecoverable after creation — so build a complete
 *  snippet here, the one moment the key actually exists client-side. */
function revealSnippet(siteId: string, writeKey: string): string {
  const h =
    typeof window !== "undefined" ? window.location.origin : "https://your-workspace.example";
  return `<!-- aigen analytics — first-party web tag -->
<script>
  var _dl = window._dl = window._dl || [];
  _dl.push(['setTrackerUrl', '${h}/analytics/v1/collect']);
  _dl.push(['setSiteId', '${siteId}']);
  _dl.push(['setWriteKey', '${writeKey}']);
  _dl.push(['trackPageView']);
  (function(){var s=document.createElement('script');
   s.async=1;s.src='${h}/analytics/v1/dl.js';document.head.appendChild(s);})();
</script>`;
}

/* ------------------------------ small pieces ------------------------------ */
function CopyButton({ text, label = "copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(text);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
      }}
      className="inline-flex shrink-0 items-center gap-1 rounded-btn px-2 py-1 font-mono text-[11px] text-subtle transition-colors hover:bg-paper-2 hover:text-ink dark:hover:bg-white/5"
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} /> copied
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" strokeWidth={1.5} /> {label}
        </>
      )}
    </button>
  );
}

/** Prominent one-time write-key callout, shown after create / rotate. */
function KeyReveal({ reveal, onDismiss }: { reveal: Reveal; onDismiss: () => void }) {
  return (
    <div className="animate-fade-up rounded-card border border-accent/40 bg-accent-soft/60 p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent/15 text-accent">
          <KeyRound className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold tracking-tight text-ink-strong">
            {reveal.reason === "created" ? "Property created" : "Key rotated"} — copy your write key now
          </p>
          <p className="mt-0.5 text-[12px] text-muted">
            This is the only time <span className="font-medium text-ink">{reveal.name}</span>&apos;s full
            key is shown. Store it somewhere safe — you can always rotate it later.
          </p>
          <div className="mt-2.5 flex items-center gap-2 rounded-btn border border-hairline bg-paper px-3 py-2 dark:bg-white/5">
            <code className="min-w-0 flex-1 truncate font-mono text-[12px] text-ink-strong">
              {reveal.key}
            </code>
            <CopyButton text={reveal.key} label="copy key" />
          </div>

          <div className="mt-3">
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-subtle">
                Paste into your site&apos;s &lt;head&gt;
              </span>
              <CopyButton text={revealSnippet(reveal.siteId, reveal.key)} label="copy snippet" />
            </div>
            <pre className="overflow-x-auto rounded-btn border border-hairline bg-paper p-3 font-mono text-[11px] leading-relaxed text-ink dark:bg-white/5">
              {revealSnippet(reveal.siteId, reveal.key)}
            </pre>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-subtle transition-colors hover:bg-paper-2 hover:text-ink dark:hover:bg-white/5"
        >
          <X className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}

/* ----------------------------- property row ------------------------------- */
function PropertyRow({ p, onReveal }: { p: PropView; onReveal: (r: Reveal) => void }) {
  const [confirm, setConfirm] = useState<null | "rotate" | "revoke">(null);
  const rotate = useRotateSiteKey();
  const revoke = useRevokeSite();
  const { label, Icon } = TYPE_META[p.type];
  const locked = p.sample || p.revoked;
  const busy = rotate.isPending || revoke.isPending;

  const doRotate = () =>
    rotate.mutate(p.siteId, {
      onSuccess: (res) => {
        onReveal({ name: p.name, siteId: p.siteId, key: res.write_key, reason: "rotated" });
        setConfirm(null);
      },
    });
  const doRevoke = () =>
    revoke.mutate(p.siteId, { onSuccess: () => setConfirm(null) });

  return (
    <div className={cn("border-b border-hairline/60 px-5 py-4 last:border-0", p.revoked && "opacity-60")}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-paper-2 text-muted dark:bg-white/5">
          <Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-[15px] font-semibold tracking-tight text-ink-strong">
              {p.name}
            </span>
            <span className="rounded-chip bg-paper-2 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-subtle dark:bg-white/5">
              {label}
            </span>
            <StatusPill tone={p.revoked ? "muted" : "ok"}>{p.revoked ? "Revoked" : "Active"}</StatusPill>
          </div>

          {/* site id */}
          <div className="mt-2.5 flex items-center gap-2 rounded-btn border border-hairline bg-paper px-3 py-2 dark:bg-white/5">
            <span className="eyebrow shrink-0">Site ID</span>
            <code className="min-w-0 flex-1 truncate font-mono text-[12px] text-ink-strong">{p.siteId}</code>
            <CopyButton text={p.siteId} />
          </div>

          {/* write key (masked — full key only shown once on create/rotate) */}
          <div className="mt-2 flex items-center gap-2 rounded-btn border border-hairline bg-paper px-3 py-2 dark:bg-white/5">
            <span className="eyebrow shrink-0">Write key</span>
            <code className="min-w-0 flex-1 truncate font-mono text-[12px] text-ink-strong">
              aig_••••••••{p.last4}
            </code>
            <span className="shrink-0 font-mono text-[10px] text-subtle">shown once</span>
          </div>

          {(p.origins.length > 0 || p.urls.length > 0) && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[...p.urls, ...p.origins].map((u) => (
                <span
                  key={u}
                  className="rounded-chip bg-paper-2 px-2 py-0.5 font-mono text-[11px] text-muted dark:bg-white/5"
                >
                  {u}
                </span>
              ))}
            </div>
          )}

          {/* actions */}
          {!locked && (
            <div className="mt-3">
              {confirm === null ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setConfirm("rotate")} disabled={busy}>
                    <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} /> Rotate key
                  </Button>
                  <button
                    type="button"
                    onClick={() => setConfirm("revoke")}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-accent disabled:opacity-50"
                  >
                    <Ban className="h-3.5 w-3.5" strokeWidth={1.75} /> Revoke
                  </button>
                </div>
              ) : (
                <div className="animate-fade-up rounded-btn border border-hairline bg-paper-2/60 px-3 py-2.5 dark:bg-white/5">
                  <p className="text-[13px] text-ink">
                    {confirm === "rotate"
                      ? "Rotate the write key? The current key stops working immediately — update your install before traffic resumes."
                      : "Revoke this property? Its keys stop working immediately and it disappears from your live data."}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <Button
                      variant={confirm === "rotate" ? "coral" : "ink"}
                      size="sm"
                      onClick={confirm === "rotate" ? doRotate : doRevoke}
                      disabled={busy}
                    >
                      {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} />}
                      {confirm === "rotate"
                        ? busy
                          ? "Rotating…"
                          : "Rotate key"
                        : busy
                          ? "Revoking…"
                          : "Revoke property"}
                    </Button>
                    <Button variant="quiet" size="sm" onClick={() => setConfirm(null)} disabled={busy}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {(rotate.isError || revoke.isError) && (
                <p className="mt-2 text-[12px] text-accent">
                  {errMessage(
                    rotate.error ?? revoke.error,
                    "Something went wrong. Please try again.",
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- section ---------------------------------- */
export function WebPropertiesSection() {
  const ws = useWorkspace();
  const sitesQ = useSites();
  const create = useCreateSite();

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState<SiteType>("web");
  const [reveal, setReveal] = useState<Reveal | null>(null);

  const realSites = sitesQ.data?.sites;
  const isSample = !ws.live || !!sitesQ.error || !realSites;
  const list: PropView[] = isSample ? ws.properties.map(sampleView) : realSites!.map(liveView);

  const closeForm = () => {
    setAdding(false);
    setName("");
    setType("web");
    create.reset();
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n || create.isPending) return;
    create.mutate(
      { name: n, type },
      {
        onSuccess: (site) => {
          setReveal({ name: site.name, siteId: site.site_id, key: site.write_key, reason: "created" });
          closeForm();
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      {reveal && <KeyReveal reveal={reveal} onDismiss={() => setReveal(null)} />}

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-hairline px-5 py-4">
          <div>
            <p className="eyebrow mb-1 flex items-center gap-2">
              Web properties {isSample && <SampleChip />}
            </p>
            <h3 className="text-[15px] font-semibold tracking-tight text-ink-strong">
              Your tracked sites &amp; apps and their keys
            </h3>
          </div>
          {ws.live && !adding && (
            <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.75} /> Add property
            </Button>
          )}
        </div>

        {adding && (
          <form
            onSubmit={submit}
            className="animate-fade-up border-b border-hairline bg-paper-2/60 px-5 py-4 dark:bg-white/5"
          >
            <label className="eyebrow mb-1.5 block">New property name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Marketing site"
              className="h-11 w-full rounded-btn border border-hairline bg-paper px-3 text-[14px] text-ink outline-none placeholder:text-subtle focus:border-accent dark:bg-white/5"
            />
            <div className="mt-3">
              <span className="eyebrow mb-1.5 block">Type</span>
              <div className="flex flex-wrap gap-1.5">
                {TYPE_ORDER.map((t) => {
                  const { label, Icon } = TYPE_META[t];
                  const sel = t === type;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setType(t)}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-btn border px-3 py-1.5 text-[13px] transition-colors",
                        sel
                          ? "border-accent bg-accent-soft text-accent"
                          : "border-hairline text-muted hover:text-ink",
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button type="submit" variant="coral" size="md" disabled={!name.trim() || create.isPending}>
                {create.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />}
                {create.isPending ? "Creating…" : "Create property"}
              </Button>
              <Button type="button" variant="quiet" size="md" onClick={closeForm} disabled={create.isPending}>
                Cancel
              </Button>
            </div>
            {create.isError ? (
              <p className="mt-2 text-[12px] text-accent">
                {errMessage(create.error, "Couldn't create the property. Try again.")}
              </p>
            ) : (
              <p className="mt-2 text-[12px] text-subtle">
                We&apos;ll generate a site ID and write key — the full key is shown once, right after.
              </p>
            )}
          </form>
        )}

        {sitesQ.isLoading && ws.live ? (
          <div className="space-y-3 px-5 py-4">
            {[0, 1].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-paper-2 dark:bg-white/5" />
                <div className="flex-1 space-y-1.5">
                  <SkeletonLine className="h-3.5 w-44" />
                  <SkeletonLine className="h-3 w-64" />
                </div>
              </div>
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <Globe className="mx-auto mb-2 h-6 w-6 text-subtle" strokeWidth={1.5} />
            <p className="text-[14px] text-muted">No properties yet</p>
            <p className="mt-0.5 text-[12px] text-subtle">
              Add one above to start tracking a site or app.
            </p>
          </div>
        ) : (
          <div>
            {list.map((p) => (
              <PropertyRow key={p.siteId} p={p} onReveal={setReveal} />
            ))}
          </div>
        )}

        {isSample && (
          <p className="border-t border-hairline px-5 py-3 text-[12px] text-subtle">
            Sample properties shown — sign in to create live properties and manage their keys.
          </p>
        )}
      </Card>
    </div>
  );
}
